import React, { useState } from 'react';
import { ulid } from 'ulid';
import Accordion, { Panel } from '../../../Components/Containers/Accordion';
import Icon from '../../../Components/Icons/Icon';

// Grouped service picker for the onboarding wizard. Deliberately not the
// `Services` form field (which is coupled to the authenticated /services
// API) - selections live in the wizard draft until registration.
//
// value: {selected: string[], custom: [{id, category, title}]}
const WizardServices = ({value, onChange, predefined}) => {
    const {selected = [], custom = []} = value ?? {};

    const toggle = (key) => {
        onChange({
            selected: selected.includes(key)
                ? selected.filter(s => s !== key)
                : [...selected, key],
            custom,
        });
    };

    const addCustom = (category, title) => {
        const trimmed = title.trim();
        if (!trimmed) {
            return;
        }

        const service = {id: ulid(), category, title: trimmed};

        onChange({
            selected: [...selected, service.id],
            custom: [...custom, service],
        });
    };

    const removeCustom = (id) => {
        onChange({
            selected: selected.filter(s => s !== id),
            custom: custom.filter(c => c.id !== id),
        });
    };

    // The first category (Exterior Cleaning) opens by default
    return <Accordion openFirstPanel>
        {predefined.map(category => {
            const customForCategory = custom.filter(c => c.category === category.category);
            const selectedCount = category.services.filter(s => selected.includes(s.type)).length
                + customForCategory.filter(c => selected.includes(c.id)).length;

            return <Panel
                key={category.category}
                title={category.category + (selectedCount > 0 ? ` (${selectedCount})` : '')}
            >
                <div className="space-y-3 py-1">
                    {category.services.map(service => <CheckboxRow
                        key={service.type}
                        id={`wizard-service-${service.type}`}
                        label={service.title}
                        checked={selected.includes(service.type)}
                        onChange={() => toggle(service.type)}
                    />)}

                    {customForCategory.map(service => <CheckboxRow
                        key={service.id}
                        id={`wizard-service-${service.id}`}
                        label={service.title}
                        checked={selected.includes(service.id)}
                        onChange={() => toggle(service.id)}
                        onRemove={() => removeCustom(service.id)}
                    />)}

                    <AddCustomRow category={category.category} onAdd={addCustom} />
                </div>
            </Panel>;
        })}
    </Accordion>;
};

const CheckboxRow = ({id, label, checked, onChange, onRemove}) => {
    return <div className="relative flex items-start">
        <div className="flex h-6 items-center">
            <input
                id={id}
                type="checkbox"
                className="ml-1 h-4 w-4 rounded-sm border-muted text-brand focus:ring-brand"
                checked={checked}
                onChange={onChange}
            />
        </div>
        <div className="ml-3 grow text-sm leading-6 flex items-center justify-between">
            <label htmlFor={id} className="font-medium">{label}</label>
            {onRemove && <button
                type="button"
                onClick={onRemove}
                className="text-xs fill-muted hover:fill-red-500 cursor-pointer"
            >
                <Icon icon="XMark" />
            </button>}
        </div>
    </div>;
};

const AddCustomRow = ({category, onAdd}) => {
    const [title, setTitle] = useState('');

    const commit = () => {
        onAdd(category, title);
        setTitle('');
    };

    const id = `wizard-service-other-${category.replace(/[^a-zA-Z]/g, '')}`;

    return <div className="relative flex items-start">
        <div className="flex h-6 items-center">
            <input
                id={id}
                type="checkbox"
                className="ml-1 h-4 w-4 rounded-sm border-muted text-brand focus:ring-brand"
                checked={false}
                onChange={() => document.getElementById(`${id}-field`)?.focus()}
            />
        </div>
        <div className="ml-3 grow text-sm leading-6">
            <label htmlFor={id} className="sr-only">Add your own {category} service</label>
            <input
                id={`${id}-field`}
                className="block w-full border-0 p-0 surface-1 focus:ring-0 sm:text-sm sm:leading-6"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        commit();
                    }
                }}
                placeholder="Add your own…"
            />
        </div>
    </div>;
};

export default WizardServices;
